import { useCallback, useEffect, useRef, useState } from "react";
import { patchFeedbackStore, readFeedbackStore } from "./feedbackStore";

/** How long a dismissal silences the nudge for the form it was recorded on. */
export const NUDGE_DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export type FeedbackNudgeState = {
  /** The `ACTIVE_FEEDBACK_FORM.key` this record describes. */
  formKey: string | null;
  /**
   * When "Not now", Escape, or click-through was last used, as epoch ms.
   * Click-through is a deferral, not a submit — it costs the same 24 hours.
   */
  lastDismissedAt: number | null;
  /**
   * When the bubble was last put on screen, as epoch ms. Ignoring an offer
   * costs the same cooldown as declining one — without this a bubble left
   * alone reappears on the next trigger after any reload.
   */
  lastShownAt: number | null;
};

export const emptyNudgeState = (
  formKey: string | null,
): FeedbackNudgeState => ({
  formKey,
  lastDismissedAt: null,
  lastShownAt: null,
});

/**
 * The record as it applies to `formKey`. A record kept against a different form
 * describes a question that is no longer being asked, so it reads as empty —
 * the same terms on which the footer button returns for an unrecorded form.
 */
const forForm = (
  state: FeedbackNudgeState,
  formKey: string | null,
): FeedbackNudgeState =>
  state.formKey === formKey ? state : emptyNudgeState(formKey);

export const canShowNudge = (
  state: FeedbackNudgeState,
  now: number,
  formKey: string | null,
): boolean => {
  const current = forForm(state, formKey);
  const lastAsked = Math.max(
    current.lastShownAt ?? 0,
    current.lastDismissedAt ?? 0,
  );
  return lastAsked === 0 || now - lastAsked >= NUDGE_DISMISS_COOLDOWN_MS;
};

export const recordNudgeShown = (
  state: FeedbackNudgeState,
  at: number,
  formKey: string | null,
): FeedbackNudgeState => ({ ...forForm(state, formKey), lastShownAt: at });

export const recordNudgeDismissed = (
  state: FeedbackNudgeState,
  at: number,
  formKey: string | null,
): FeedbackNudgeState => ({
  ...forForm(state, formKey),
  lastDismissedAt: at,
});

/**
 * Has this browser already submitted `formKey`? `lastFormKey` is written only
 * by a confirmed submit (or a server "already submitted"), so it is the
 * terminal signal — not click-through.
 */
export const hasCachedSubmission = (
  currentUserId: string | null,
  formKey: string | null,
): boolean =>
  formKey !== null && readFeedbackStore(currentUserId).lastFormKey === formKey;

/**
 * The stored record, validated and re-keyed to `formKey`. A half-written or
 * hand-edited value degrades to "ask from the start" rather than throwing or
 * becoming a permanent silence.
 */
const epochOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export const readNudgeState = (
  currentUserId: string | null,
  formKey: string | null,
): FeedbackNudgeState => {
  const stored = readFeedbackStore(currentUserId).nudge as unknown;
  if (!stored || typeof stored !== "object") {
    return emptyNudgeState(formKey);
  }

  const raw = stored as Partial<Record<keyof FeedbackNudgeState, unknown>>;
  return forForm(
    {
      formKey: typeof raw.formKey === "string" ? raw.formKey : null,
      lastDismissedAt: epochOrNull(raw.lastDismissedAt),
      lastShownAt: epochOrNull(raw.lastShownAt),
    },
    formKey,
  );
};

export const writeNudgeState = (
  currentUserId: string | null,
  nudge: FeedbackNudgeState,
) => patchFeedbackStore(currentUserId, { nudge });

/**
 * A module-level bus, rather than a prop drilled through the editor: the
 * producers (cloud save / autosave) sit nowhere near the footer that consumes
 * them, and need not know a nudge exists.
 */
const listeners = new Set<() => void>();

/**
 * When a trigger last fired, held outside React so it survives the remount a
 * file-creating save causes when the route switches to `/f/<id>`.
 *
 * `offerHeld` is the same trigger after it has been shown: a remount must put
 * the bubble back without treating that as a fresh ask, even if a later
 * autosave stamped a newer `pendingSince`.
 */
let pendingSince: number | null = null;
let offerHeld = false;

const clearPending = () => {
  pendingSince = null;
  offerHeld = false;
};

/** Announce a moment worth asking on. */
export const fireNudgeTrigger = () => {
  pendingSince = Date.now();
  listeners.forEach((listener) => listener());
};

/** Forget any trigger still waiting: opening the form is itself engagement. */
export const resetPendingTrigger = () => {
  clearPending();
  listeners.forEach((listener) => listener());
};

export interface UseFeedbackNudgeOptions {
  userId: string | null;
  /** False once they have submitted, or while anything else owns the screen. */
  eligible: boolean;
  /** The form being asked about; every record is keyed to it. */
  formKey: string | null;
}

/**
 * Decides whether the feedback coach-mark is on screen right now.
 *
 * A trigger is an offer, not an instruction: it is shown as soon as we are
 * eligible. It stays pending across blockers (onboarding, auth) until then,
 * or until the form is opened / they defer. Frequency is the 24-hour cooldown,
 * not how old the save was.
 */
export const useFeedbackNudge = ({
  userId,
  eligible,
  formKey,
}: UseFeedbackNudgeOptions) => {
  const [visible, setVisible] = useState(false);

  // Kept in refs so the subscription below never re-binds; re-binding on every
  // render would drop triggers fired mid-commit.
  const eligibleRef = useRef(eligible);
  eligibleRef.current = eligible;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const formKeyRef = useRef(formKey);
  formKeyRef.current = formKey;
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  // Lets the eligibility effect re-run the decision without re-binding the
  // trigger subscription.
  const considerRef = useRef<() => void>(() => {});

  useEffect(() => {
    const consider = () => {
      const now = Date.now();
      if (pendingSince === null) {
        if (visibleRef.current) {
          setVisible(false);
        }
        return;
      }
      if (visibleRef.current) {
        return;
      }
      // Eligibility reads false until auth settles and the lookup answers.
      // Keep the trigger pending and let the effect below retry.
      if (!eligibleRef.current) {
        return;
      }
      // A confirmed submit is terminal for this form, including a remount
      // that still holds an in-flight offer.
      if (hasCachedSubmission(userIdRef.current, formKeyRef.current)) {
        clearPending();
        return;
      }
      // A file-creating save remounts the footer as the route switches to
      // `/f/<id>`. Put the bubble back without treating that as a fresh ask.
      if (offerHeld) {
        setVisible(true);
        return;
      }
      const state = readNudgeState(userIdRef.current, formKeyRef.current);
      if (!canShowNudge(state, now, formKeyRef.current)) {
        clearPending();
        return;
      }
      // Recorded here rather than from the bubble's mount effect: being asked
      // is what starts the cooldown, whether or not they answer.
      writeNudgeState(
        userIdRef.current,
        recordNudgeShown(state, now, formKeyRef.current),
      );
      offerHeld = true;
      setVisible(true);
    };
    considerRef.current = consider;

    listeners.add(consider);
    // Pick up a trigger that outlived the previous mount.
    consider();

    return () => {
      listeners.delete(consider);
    };
  }, []);

  // Eligibility resolves asynchronously, so a trigger can arrive before the
  // answer does. Re-decide the moment it turns true; hide while anything else
  // owns the screen. Drop `offerHeld` here so a DND hide (onboarding modal)
  // does not restore the bubble the instant the blocker lifts — remounts
  // never take this branch, so `/f/<id>` still puts a live offer back.
  useEffect(() => {
    if (eligible) {
      considerRef.current();
    } else if (visibleRef.current) {
      offerHeld = false;
      setVisible(false);
    }
  }, [eligible]);

  const close = useCallback(
    (next: (state: FeedbackNudgeState) => FeedbackNudgeState) => {
      clearPending();
      setVisible(false);
      const id = userIdRef.current;
      const key = formKeyRef.current;
      writeNudgeState(id, next(readNudgeState(id, key)));
    },
    [],
  );

  /**
   * "Not now", Escape, and click-through all defer 24 hours. Only a submit
   * (cached as `lastFormKey`) stops asking for this form.
   */
  const onDefer = useCallback(
    () =>
      close((state) =>
        recordNudgeDismissed(state, Date.now(), formKeyRef.current),
      ),
    [close],
  );

  return { visible, onDismiss: onDefer, onAccept: onDefer };
};
